import { ReactComponent as IconClose } from "@/assets/svgs/icon-close.svg";
import CardConfigure from "@/components/CardConfigure";
import Modal from "@/components/Modal2";
import DSModal from "@/componentsDS/Modal/Modal.jsx";
import DSCardConfigure from "@/componentsDS/Modal/CardConfigure.jsx";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import JsonViewer from "./components/JsonViewer";
import JsonUpload from "./components/JsonUpload";
import JsonDownload from "./components/JsonDownload";
import XAllOfBuilder, { emitRule, rulesToXAllOf, xAllOfToRules } from "./components/xallof";
import ValidationPreview from "./components/ValidationPreview";
import {
	SAMPLE_VALUES,
	addChildToField,
	buildJsonSchema,
	buildPayloadTemplate,
	buildQuerySchemaFromElementSchema,
	diffAgainstBase,
	diffXAllOfRules,
	formatDataType,
	formatExampleValueForInput,
	getFieldDepth,
	parseExampleValue,
	removeFieldFromTree,
	sortFieldsAttachmentsLast,
	updateFieldInTree,
	validateSchemaShape,
} from "./dsv-schema-utils";
import { summarize as summarizeRule } from "./components/xallof/summarize";
import useDataSchemaVersioning from "@/hooks/use-data-schema-versioning/index";
import { extractSchemaFields } from "@/hooks/use-data-schema-versioning/index";
import useModal from "@/hooks/use-modal";
import usePage from "@/hooks/use-page";
import useToast from "@/hooks/use-toast";
import Toast from "@/components/Toast";
import { useNavigate, useParams } from "@/lib/router-dom";
import rem from "@/utils/rem.js";
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

// ============ Constants matching BE ============

const CHANGE_TYPES = [
	{ name: 'Patch (Bug fixes)', value: 'PATCH' },
	{ name: 'Minor (New features)', value: 'MINOR' },
	{ name: 'Major (Breaking changes)', value: 'MAJOR' },
];

const DATA_TYPES = [
	{ name: 'string', value: 'string' },
	{ name: 'integer', value: 'integer' },
	{ name: 'number', value: 'number' },
	{ name: 'boolean', value: 'boolean' },
	{ name: 'date', value: 'date' },
	{ name: 'date-time', value: 'date-time' },
	{ name: 'array', value: 'array' },
	{ name: 'object', value: 'object' },
];

const MAX_NESTING_DEPTH = 6;

// ============ Transition Config ============

const TRANSITION_CONFIG = {
	DRAFT: {
		targetStatus: 'ACTIVE',
		buttonLabel: 'Promote to Active',
		buttonClass: 'dsv-btn--promote',
		modalTitle: 'Promote Version',
		verb: 'promoted to Active',
		showExpiryDate: () => true,
		expiryRequired: (version) => version.changeType === 'MAJOR',
		expiryHint: 'Required for MAJOR versions (min 7 days from today). Optional for Minor/Patch — auto-inherits from parent MAJOR.',
		confirmationMessage: null,
		getDisabledReason: () => null,
		getEnabledHint: () => null,
	},
	ACTIVE: {
		targetStatus: 'DEPRECATED',
		buttonLabel: 'Deprecate',
		buttonClass: 'dsv-btn--deprecate',
		modalTitle: 'Deprecate Version',
		verb: 'deprecated',
		showExpiryDate: () => true,
		expiryRequired: () => false,
		expiryHint: 'Optionally set an expiry date for the deprecation period. Defaults to 30 days.',
		confirmationMessage: 'This version will be marked as deprecated. Consumers should migrate to a newer version.',
		getDisabledReason: () => null,
		getEnabledHint: () => null,
	},
	DEPRECATED: {
		targetStatus: 'RETIRED',
		buttonLabel: 'Retire',
		buttonClass: 'dsv-btn--retire',
		modalTitle: 'Retire Version',
		verb: 'retired',
		showExpiryDate: () => false,
		expiryRequired: () => false,
		expiryHint: null,
		confirmationMessage: 'This version will be permanently retired and cannot be undone. For MAJOR versions, this may fail if the expiry date has not yet passed.',
		getDisabledReason: (version) => {
			if (version.changeType === 'MAJOR' && version.expiry && new Date(version.expiry) > new Date()) {
				return `Cannot retire MAJOR version before expiry date (${formatDate(version.expiry)})`;
			}
			return null;
		},
		getEnabledHint: (version) => {
			if (version.changeType !== 'MAJOR' && version.expiry && new Date(version.expiry) > new Date()) {
				return `${version.changeType} versions can be retired before expiry`;
			}
			return null;
		},
	},
};

function getTransitionConfig(status) {
	return TRANSITION_CONFIG[status] || null;
}

function formatDate(dateStr) {
	if (!dateStr) return '-';
	return new Date(dateStr).toISOString().split('T')[0];
}

function getDefaultExpiryDate() {
	const date = new Date();
	date.setMonth(date.getMonth() + 1);
	return date.toISOString().split('T')[0];
}

// ============ Status Badges ============

function StatusBadge({ status }) {
	const statusMap = {
		'ACTIVE': { className: 'dsv-status--active', label: 'Active' },
		'DRAFT': { className: 'dsv-status--draft', label: 'Draft' },
		'DEPRECATED': { className: 'dsv-status--migrating', label: 'Deprecated' },
		'RETIRED': { className: 'dsv-status--retired', label: 'Retired' },
	};
	const config = statusMap[status] || statusMap['DRAFT'];
	return <span className={`dsv-status ${config.className}`}>{config.label}</span>;
}

StatusBadge.propTypes = { status: PropTypes.string };

function FieldStatusBadge({ status }) {
	if (!status) return null;
	const statusMap = {
		'Modified': { className: 'dsv-field-status--modified', label: 'Modified' },
		'New': { className: 'dsv-field-status--new', label: 'New' },
		'Deleted': { className: 'dsv-field-status--deleted', label: 'Deleted' },
	};
	const config = statusMap[status];
	if (!config) return null;
	return <span className={`dsv-field-status ${config.className}`}>{config.label}</span>;
}

FieldStatusBadge.propTypes = { status: PropTypes.string };

function ChangeTypeBadge({ changeType }) {
	const typeMap = {
		'PATCH': { className: 'dsv-change-type--patch', label: 'Patch' },
		'MINOR': { className: 'dsv-change-type--minor', label: 'Minor' },
		'MAJOR': { className: 'dsv-change-type--major', label: 'Major' },
		'INITIAL': { className: 'dsv-change-type--initial', label: 'Initial' },
	};
	const config = typeMap[changeType] || typeMap['INITIAL'];
	return <span className={`dsv-change-type ${config.className}`}>{config.label}</span>;
}

ChangeTypeBadge.propTypes = { changeType: PropTypes.string };

// ============ Promote Modal ============

function PromoteModal({ visible, hide, version, onConfirm }) {
	const [expiryDate, setExpiryDate] = useState(getDefaultExpiryDate);

	useEffect(() => {
		if (visible) {
			setExpiryDate(getDefaultExpiryDate());
		}
	}, [visible]);

	if (!version) return null;

	const config = getTransitionConfig(version.status);
	if (!config) return null;

	const shouldShowExpiry = config.showExpiryDate(version);
	const isExpiryRequired = config.expiryRequired(version);

	const minDate = (version.status === 'DRAFT' && version.changeType === 'MAJOR')
		? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
		: new Date().toISOString().split('T')[0];

	function handleConfirm() {
		const expiry = shouldShowExpiry ? (expiryDate || null) : null;
		onConfirm(config.targetStatus, expiry);
	}

	return (
		<DSModal visible={visible} close={hide} variant="small" dismissClickOutsideModal={false}>
			<DSCardConfigure style={{ minWidth: 600 }}>
				<DSCardConfigure.Header>
					<DSCardConfigure.Header.Title>{config.modalTitle}</DSCardConfigure.Header.Title>
					<DSCardConfigure.Header.Close>
						<button className="dsv-icon-btn" onClick={hide} title="Close">&times;</button>
					</DSCardConfigure.Header.Close>
				</DSCardConfigure.Header>
				<DSCardConfigure.Body>
					<DSCardConfigure.Body.Col>
						<p className="dsv-confirm__subtitle">
							Promote <strong>v{version.version}</strong> from{' '}
							<StatusBadge status={version.status} /> to{' '}
							<StatusBadge status={config.targetStatus} />
						</p>

						<div className="dsv-confirm__section">
							<h3 className="dsv-confirm__section-title">Version Details</h3>
							<div className="dsv-confirm__details">
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Version</span>
									<span className="dsv-confirm__value">v{version.version}</span>
								</div>
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Change Type</span>
									<span className="dsv-confirm__value"><ChangeTypeBadge changeType={version.changeType} /></span>
								</div>
								{version.changeDescription && (
									<div className="dsv-confirm__row">
										<span className="dsv-confirm__label">Description</span>
										<span className="dsv-confirm__value">{version.changeDescription}</span>
									</div>
								)}
							</div>
						</div>

						{config.confirmationMessage && (
							<div className="dsv-confirm__section">
								<p className="dsv-promote__hint" style={{ color: '#b45309' }}>
									{config.confirmationMessage}
								</p>
							</div>
						)}

						{shouldShowExpiry && (
							<div className="dsv-confirm__section">
								<h3 className="dsv-confirm__section-title">
									Expiry Date{isExpiryRequired ? '' : ' (Optional)'}
								</h3>
								<p className="dsv-promote__hint">
									{config.expiryHint}
								</p>
								<input
									className="dsv-modal__input"
									type="date"
									value={expiryDate}
									min={minDate}
									onChange={(e) => setExpiryDate(e.target.value)}
								/>
							</div>
						)}
					</DSCardConfigure.Body.Col>
				</DSCardConfigure.Body>
				<DSCardConfigure.Footer>
					<DSCardConfigure.Footer.Left />
					<DSCardConfigure.Footer.Right>
						<button className="dsv-btn dsv-btn--ghost" onClick={hide}>Cancel</button>
						<button className={`dsv-btn ${config.buttonClass}`} style={{ marginLeft: rem(12) }} onClick={handleConfirm}>
							{config.buttonLabel}
						</button>
					</DSCardConfigure.Footer.Right>
				</DSCardConfigure.Footer>
			</DSCardConfigure>
		</DSModal>
	);
}

PromoteModal.propTypes = {
	visible: PropTypes.bool,
	hide: PropTypes.func,
	version: PropTypes.object,
	onConfirm: PropTypes.func,
};

// ============ Schema Fields Table ============

function SchemaFieldRow({ field, depth, onEditField, onDeleteField, onAddChild, canEditSchema }) {
	const hasChildren = field.children && field.children.length > 0;
	const [expanded, setExpanded] = useState(hasChildren);
	const canAddChild = canEditSchema && field.status !== 'Deleted' && (
		field.dataType === 'object' ||
		(field.dataType === 'array' && field.arrayItemType === 'object')
	);

	// Auto-expand when children are added
	useEffect(() => {
		if (hasChildren) setExpanded(true);
	}, [field.children?.length]);

	return (
		<>
			<div
				className={`dsv-schema__table-row ${field.status ? `dsv-schema__table-row--${field.status.toLowerCase()}` : ''}`}
			>
				<div className="dsv-schema__table-cell dsv-schema__col--name" title={field.dataElementName}>
					<div style={{ paddingLeft: depth * 20 + (hasChildren ? 0 : 20) }}>
						{hasChildren && (
							<button type="button" className="dsv-schema__toggle" style={{ fontSize: 16, cursor: 'pointer', background: 'none', border: 'none', padding: 0 }} onClick={() => setExpanded(prev => !prev)} aria-label={expanded ? 'Collapse' : 'Expand'}>
								{expanded ? '▾' : '▸'}
							</button>
						)}
						{field.dataElementName}
						<FieldStatusBadge status={field.status} />
					</div>
				</div>
				<div className="dsv-schema__table-cell dsv-schema__col--business" title={field.businessTerm}>
					{field.businessTerm}
				</div>
				<div className="dsv-schema__table-cell dsv-schema__col--mandatory">
					<span className={`dsv-mandatory-badge ${field.mandatory ? 'dsv-mandatory-badge--mandatory' : 'dsv-mandatory-badge--optional'}`}>
						{field.mandatory ? 'Mandatory' : 'Optional'}
					</span>
				</div>
				<div className="dsv-schema__table-cell dsv-schema__col--datatype">
					{field.dataType === 'array' && field.arrayItemType
						? `array<${field.arrayItemType}>`
						: field.dataType}
				</div>
				{canEditSchema && (
					<div className="dsv-schema__table-cell dsv-schema__col--field-actions">
						{canAddChild && (
							<button className="dsv-icon-btn" onClick={() => onAddChild(field)} title="Add child field">
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
									<path d="M8 3.333V12.667" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
									<path d="M3.333 8H12.667" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</button>
						)}
						<button className="dsv-icon-btn" onClick={() => onEditField(field)} title="Edit field">
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
								<path d="M11.333 2C11.508 1.825 11.716 1.686 11.945 1.591C12.174 1.497 12.419 1.448 12.666 1.448C12.914 1.448 13.159 1.497 13.388 1.591C13.617 1.686 13.825 1.825 14 2C14.175 2.175 14.314 2.383 14.408 2.612C14.503 2.841 14.552 3.086 14.552 3.333C14.552 3.581 14.503 3.826 14.408 4.055C14.314 4.284 14.175 4.492 14 4.667L5 13.667L1.333 14.667L2.333 11L11.333 2Z" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
						<button className="dsv-icon-btn dsv-icon-btn--delete" onClick={() => onDeleteField(field)} title="Delete field">
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
								<path d="M2 4H14" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
								<path d="M5.333 4V2.667C5.333 2.313 5.473 1.974 5.724 1.724C5.974 1.474 6.313 1.333 6.666 1.333H9.333C9.687 1.333 10.026 1.474 10.276 1.724C10.526 1.974 10.666 2.313 10.666 2.667V4M12.666 4V13.333C12.666 13.687 12.526 14.026 12.276 14.276C12.026 14.526 11.687 14.667 11.333 14.667H4.666C4.313 14.667 3.974 14.526 3.724 14.276C3.473 14.026 3.333 13.687 3.333 13.333V4H12.666Z" stroke="#6B7280" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</button>
					</div>
				)}
			</div>
			{expanded && hasChildren && field.children.map((child) => (
				<SchemaFieldRow
					key={child.fieldId}
					field={child}
					depth={depth + 1}
					onEditField={onEditField}
					onDeleteField={onDeleteField}
					onAddChild={onAddChild}
					canEditSchema={canEditSchema}
				/>
			))}
		</>
	);
}

SchemaFieldRow.propTypes = {
	field: PropTypes.object,
	depth: PropTypes.number,
	onEditField: PropTypes.func,
	onDeleteField: PropTypes.func,
	onAddChild: PropTypes.func,
	canEditSchema: PropTypes.bool,
};

function SchemaFieldsTable({ fields, onEditField, onDeleteField, onAddChild, canEditSchema }) {
	return (
		<div className="dsv-schema__fields">
			<div className="dsv-schema__fields-header">
				<h3 className="dsv-schema__fields-title">
					Schema Fields ({fields.filter(f => f.status !== 'Deleted').length})
				</h3>
				<p className="dsv-schema__fields-subtitle">
					{canEditSchema ? 'Manage data element fields and their properties' : 'View data element fields and their properties'}
				</p>
			</div>

			<div className="dsv-schema__table">
				<div className="dsv-schema__table-head">
					<div className="dsv-schema__table-cell dsv-schema__col--name">Schema Key</div>
					<div className="dsv-schema__table-cell dsv-schema__col--business">Business Term</div>
					<div className="dsv-schema__table-cell dsv-schema__col--mandatory">Mandatory/Optional</div>
					<div className="dsv-schema__table-cell dsv-schema__col--datatype">Data Type</div>
					{canEditSchema && (
						<div className="dsv-schema__table-cell dsv-schema__col--field-actions">Actions</div>
					)}
				</div>

				{fields.length > 0 ? (
					sortFieldsAttachmentsLast(fields).map((field) => (
						<SchemaFieldRow
							key={field.fieldId}
							field={field}
							depth={0}
							onEditField={onEditField}
							onDeleteField={onDeleteField}
							onAddChild={onAddChild}
							canEditSchema={canEditSchema}
						/>
					))
				) : (
					<div className="dsv-schema__table-empty">No schema fields defined yet.</div>
				)}
			</div>
		</div>
	);
}

SchemaFieldsTable.propTypes = {
	fields: PropTypes.array,
	onEditField: PropTypes.func,
	onDeleteField: PropTypes.func,
	onAddChild: PropTypes.func,
	canEditSchema: PropTypes.bool,
};

// ============ Add/Edit Field Modal ============

function FieldModal({ visible, hide, field, onSave, parentFieldId, currentDepth }) {
	const [formData, setFormData] = useState({
		dataElementName: '', businessTerm: '', mandatory: true, dataType: 'string', arrayItemType: 'object', exampleValue: '',
		minLength: '', maxLength: '', minimum: '', maximum: '', exclusiveMinimum: '', exclusiveMaximum: '',
		pattern: '', enumValues: '', format: '', multipleOf: '', minItems: '', maxItems: '', uniqueItems: false,
	});
	const [error, setError] = useState(null);

	useEffect(() => {
		if (field) {
			setFormData({
				dataElementName: field.dataElementName || '',
				businessTerm: field.businessTerm || '',
				mandatory: field.mandatory !== undefined ? field.mandatory : true,
				dataType: field.dataType || 'string',
				arrayItemType: field.arrayItemType || 'object',
				exampleValue: formatExampleValueForInput(field.exampleValue),
				minLength: field.minLength ?? '',
				maxLength: field.maxLength ?? '',
				minimum: field.minimum ?? '',
				maximum: field.maximum ?? '',
				exclusiveMinimum: field.exclusiveMinimum ?? '',
				exclusiveMaximum: field.exclusiveMaximum ?? '',
				pattern: field.pattern ?? '',
				enumValues: field.enumValues ? (Array.isArray(field.enumValues) ? field.enumValues.join(', ') : field.enumValues) : '',
				format: field.format ?? '',
				multipleOf: field.multipleOf ?? '',
				minItems: field.minItems ?? '',
				maxItems: field.maxItems ?? '',
				uniqueItems: field.uniqueItems ?? false,
			});
		} else {
			setFormData({ dataElementName: '', businessTerm: '', mandatory: true, dataType: 'string', arrayItemType: 'object', exampleValue: '',
				minLength: '', maxLength: '', minimum: '', maximum: '', exclusiveMinimum: '', exclusiveMaximum: '',
				pattern: '', enumValues: '', format: '', multipleOf: '', minItems: '', maxItems: '', uniqueItems: false,
			});
		}
		setError(null);
	}, [field, visible]);

	const handleChange = (key, value) => { setFormData(prev => ({ ...prev, [key]: value })); setError(null); };

	const handleSubmit = () => {
		const saveData = {
			...formData,
			enumValues: formData.enumValues
				? formData.enumValues.split(',').map(v => v.trim()).filter(Boolean)
				: null,
			minLength: formData.minLength !== '' ? Number.parseInt(formData.minLength, 10) : null,
			maxLength: formData.maxLength !== '' ? Number.parseInt(formData.maxLength, 10) : null,
			minimum: formData.minimum !== '' ? parseFloat(formData.minimum) : null,
			maximum: formData.maximum !== '' ? parseFloat(formData.maximum) : null,
			exclusiveMinimum: formData.exclusiveMinimum !== '' ? parseFloat(formData.exclusiveMinimum) : null,
			exclusiveMaximum: formData.exclusiveMaximum !== '' ? parseFloat(formData.exclusiveMaximum) : null,
			multipleOf: formData.multipleOf !== '' ? parseFloat(formData.multipleOf) : null,
			minItems: formData.minItems !== '' ? Number.parseInt(formData.minItems, 10) : null,
			maxItems: formData.maxItems !== '' ? Number.parseInt(formData.maxItems, 10) : null,
		};
		const result = onSave({ ...saveData, fieldId: field?.fieldId || null, parentFieldId: parentFieldId || null });
		if (typeof result === 'string') {
			setError(result);
		} else if (result !== false) {
			hide();
		}
	};

	const isEdit = !!field?.fieldId;
	const depth = currentDepth || 0;

	// At max depth, prevent creating further nestable types
	const availableTypes = depth >= MAX_NESTING_DEPTH - 1
		? DATA_TYPES.filter(dt => dt.value !== 'object' && dt.value !== 'array')
		: DATA_TYPES;

	const normConstraint = (v) => {
		if (v === null || v === undefined || v === '') return '';
		if (Array.isArray(v)) return v.join(', ');
		return String(v);
	};
	const CONSTRAINT_KEYS = [
		'minLength', 'maxLength', 'minimum', 'maximum',
		'exclusiveMinimum', 'exclusiveMaximum', 'multipleOf',
		'minItems', 'maxItems', 'pattern', 'format', 'enumValues',
	];
	const constraintChanged = CONSTRAINT_KEYS.some((k) =>
		normConstraint(formData[k]) !== normConstraint(field?.[k])
	) || (formData.uniqueItems !== (field?.uniqueItems ?? false))
		|| (formData.arrayItemType !== (field?.arrayItemType || 'object'));

	const hasChanges = isEdit
		? (
			formData.dataElementName !== (field?.dataElementName || '') ||
			formData.businessTerm !== (field?.businessTerm || '') ||
			formData.mandatory !== (field?.mandatory !== undefined ? field.mandatory : true) ||
			formData.dataType !== (field?.dataType || 'string') ||
			formData.exampleValue !== formatExampleValueForInput(field?.exampleValue) ||
			constraintChanged
		)
		: (formData.dataElementName.trim() !== '' && formData.businessTerm.trim() !== '');

	const existingChildren = field?.children || [];
	const canHaveChildren = formData.dataType === 'object' || (formData.dataType === 'array' && formData.arrayItemType === 'object');

	const modalTitle = parentFieldId
		? (isEdit ? 'Edit Child Field' : 'Add Child Field')
		: (isEdit ? 'Edit Field' : 'Add New Field');

	return (
		<Modal visible={visible} close={hide}>
			<CardConfigure
				style={{ maxWidth: rem(520), minWidth: rem(520), position: "relative", borderRadius: "0" }}
				type="data-dictionary"
			>
				<CardConfigure.Header>
					<CardConfigure.Header.Icon type="false">
						<div className='card-configure__title__left--background'>
							<div className="DS__icon">
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
									<rect width="24" height="24" rx="4" fill="#EEF0FF" />
									<path d="M12 5v14M5 12h14" stroke="#4F5AED" strokeWidth="2" strokeLinecap="round" />
								</svg>
							</div>
						</div>
					</CardConfigure.Header.Icon>
					<CardConfigure.Header.Close onClick={hide}>
						<IconClose width={16} height={16} />
					</CardConfigure.Header.Close>
				</CardConfigure.Header>
				<CardConfigure.Body>
					<CardConfigure.Body.Col>
						<div className="dsv-modal__title">{modalTitle}</div>
						{parentFieldId && (
							<p className="dsv-promote__hint" style={{ marginBottom: 12 }}>
								Adding to: <strong>{parentFieldId}</strong>
							</p>
						)}
						<div className="dsv-modal__form">
							<div className="dsv-modal__field">
								<label className="dsv-modal__label">Schema Key</label>
								<input className="dsv-modal__input" type="text" value={formData.dataElementName}
									onChange={(e) => handleChange('dataElementName', e.target.value)} placeholder="e.g. SubmissionEntity" />
							</div>
							<div className="dsv-modal__field">
								<label className="dsv-modal__label">Business Term</label>
								<input className="dsv-modal__input" type="text" value={formData.businessTerm}
									onChange={(e) => handleChange('businessTerm', e.target.value)} placeholder="e.g. Submission Entity" />
							</div>
							<div className="dsv-modal__field">
								<label className="dsv-modal__label">Mandatory/Optional</label>
								<select className="dsv-modal__select" value={formData.mandatory ? 'mandatory' : 'optional'}
									onChange={(e) => handleChange('mandatory', e.target.value === 'mandatory')}>
									<option value="mandatory">Mandatory</option>
									<option value="optional">Optional</option>
								</select>
							</div>
							<div className="dsv-modal__field">
								<label className="dsv-modal__label">Data Type</label>
								<select className="dsv-modal__select" value={formData.dataType}
									onChange={(e) => handleChange('dataType', e.target.value)}>
									{availableTypes.map((dt) => (
										<option key={dt.value} value={dt.value}>{dt.name}</option>
									))}
								</select>
							</div>
							{formData.dataType === 'array' && (
								<div className="dsv-modal__field">
									<label className="dsv-modal__label">Array Item Type</label>
									<select className="dsv-modal__select" value={formData.arrayItemType}
										onChange={(e) => handleChange('arrayItemType', e.target.value)}>
										<option value="string">string</option>
										<option value="integer">integer</option>
										<option value="boolean">boolean</option>
										<option value="date">date</option>
										<option value="date-time">date-time</option>
										<option value="object">object</option>
										<option value="array">array</option>
									</select>
									<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
										{formData.arrayItemType === 'object'
											? 'Add child fields using the + button after saving.'
											: `Array items will be of type "${formData.arrayItemType}".`}
									</p>
								</div>
							)}
							{formData.dataType !== 'object' && formData.dataType !== 'array' && (
								<div className="dsv-modal__field">
									<label className="dsv-modal__label">Example Value <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span></label>
									{formData.dataType === 'boolean' ? (
										<select
											className="dsv-modal__select"
											value={formData.exampleValue}
											onChange={(e) => handleChange('exampleValue', e.target.value)}
										>
											<option value="">Select boolean value</option>
											<option value="true">true</option>
											<option value="false">false</option>
										</select>
									) : (
										<input className="dsv-modal__input" type="text" value={formData.exampleValue}
											onChange={(e) => handleChange('exampleValue', e.target.value)}
											placeholder={`e.g. ${SAMPLE_VALUES[formData.dataType] ?? 'string'}`} />
									)}
									<p className="dsv-promote__hint" style={{ marginTop: 4 }}>
										Used in the payload preview instead of the default sample value.
									</p>
								</div>
							)}
						</div>

						{/* Validation Rules (collapsible) */}
						{formData.dataType !== 'object' && (
							<details className="dsv-modal__constraints-section">
								<summary className="dsv-modal__constraints-toggle">Validation Rules</summary>
								<div className="dsv-modal__constraints-body">
									{/* String constraints */}
									{(formData.dataType === 'string' || formData.dataType === 'date' || formData.dataType === 'date-time') && (
										<>
											<div className="dsv-modal__field-row">
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Min Length</label>
													<input className="dsv-modal__input" type="number" min="0" value={formData.minLength}
														onChange={(e) => handleChange('minLength', e.target.value)} />
												</div>
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Max Length</label>
													<input className="dsv-modal__input" type="number" min="0" value={formData.maxLength}
														onChange={(e) => handleChange('maxLength', e.target.value)} />
												</div>
											</div>
											<div className="dsv-modal__field">
												<label className="dsv-modal__label">Pattern (regex)</label>
												<input className="dsv-modal__input" type="text" value={formData.pattern}
													onChange={(e) => handleChange('pattern', e.target.value)} placeholder="e.g. ^[A-Z]{2}\d{4}$" />
											</div>
											<div className="dsv-modal__field">
												<label className="dsv-modal__label">Format</label>
												<select className="dsv-modal__select" value={formData.format}
													onChange={(e) => handleChange('format', e.target.value)}>
													<option value="">None</option>
													<option value="email">email</option>
													<option value="uri">uri</option>
													<option value="uuid">uuid</option>
													<option value="date">date</option>
													<option value="date-time">date-time</option>
												</select>
											</div>
										</>
									)}
									{/* Integer/number constraints */}
									{(formData.dataType === 'integer' || formData.dataType === 'number') && (
										<>
											<div className="dsv-modal__field-row">
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Minimum</label>
													<input className="dsv-modal__input" type="number" value={formData.minimum}
														onChange={(e) => handleChange('minimum', e.target.value)} />
												</div>
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Maximum</label>
													<input className="dsv-modal__input" type="number" value={formData.maximum}
														onChange={(e) => handleChange('maximum', e.target.value)} />
												</div>
											</div>
											<div className="dsv-modal__field-row">
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Exclusive Minimum</label>
													<input className="dsv-modal__input" type="number" value={formData.exclusiveMinimum}
														onChange={(e) => handleChange('exclusiveMinimum', e.target.value)} />
												</div>
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Exclusive Maximum</label>
													<input className="dsv-modal__input" type="number" value={formData.exclusiveMaximum}
														onChange={(e) => handleChange('exclusiveMaximum', e.target.value)} />
												</div>
											</div>
											<div className="dsv-modal__field">
												<label className="dsv-modal__label">Multiple Of</label>
												<input className="dsv-modal__input" type="number" step="any" value={formData.multipleOf}
													onChange={(e) => handleChange('multipleOf', e.target.value)} />
											</div>
										</>
									)}
									{/* Array constraints */}
									{formData.dataType === 'array' && (
										<>
											<div className="dsv-modal__field-row">
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Min Items</label>
													<input className="dsv-modal__input" type="number" min="0" value={formData.minItems}
														onChange={(e) => handleChange('minItems', e.target.value)} />
												</div>
												<div className="dsv-modal__field dsv-modal__field--half">
													<label className="dsv-modal__label">Max Items</label>
													<input className="dsv-modal__input" type="number" min="0" value={formData.maxItems}
														onChange={(e) => handleChange('maxItems', e.target.value)} />
												</div>
											</div>
											<div className="dsv-modal__field">
												<label className="dsv-modal__label">
													<input type="checkbox" checked={formData.uniqueItems}
														onChange={(e) => handleChange('uniqueItems', e.target.checked)} style={{ marginRight: 8 }} />
													Unique Items
												</label>
											</div>
										</>
									)}
									{/* Enum (all types except object/array) */}
									{formData.dataType !== 'array' && (
										<div className="dsv-modal__field">
											<label className="dsv-modal__label">Enum Values <span style={{ fontWeight: 400, color: '#6b7280' }}>(comma-separated)</span></label>
											<input className="dsv-modal__input" type="text" value={formData.enumValues}
												onChange={(e) => handleChange('enumValues', e.target.value)} placeholder="e.g. US, CA, SG" />
										</div>
									)}
								</div>
							</details>
						)}

						{/* Children summary for existing object/array fields */}
						{isEdit && canHaveChildren && (
							<div className="dsv-modal__children-section">
								<div className="dsv-modal__children-title">
									Child Fields ({existingChildren.filter(c => c.status !== 'Deleted').length})
								</div>
								{existingChildren.filter(c => c.status !== 'Deleted').length > 0 ? (
									<div className="dsv-modal__children-list">
										{existingChildren.filter(c => c.status !== 'Deleted').map(child => (
											<div key={child.fieldId} className="dsv-modal__children-item">
												<span className="dsv-modal__children-item-key">{child.dataElementName}</span>
												<span className="dsv-modal__children-item-type">{child.dataType}</span>
												<span className={`dsv-mandatory-badge ${child.mandatory ? 'dsv-mandatory-badge--mandatory' : 'dsv-mandatory-badge--optional'}`} style={{ fontSize: 11 }}>
													{child.mandatory ? 'M' : 'O'}
												</span>
											</div>
										))}
									</div>
								) : (
									<p className="dsv-modal__children-empty">No child fields defined. Use the + button in the schema table to add children.</p>
								)}
							</div>
						)}

						{/* Hint for new object/array fields */}
						{!isEdit && canHaveChildren && (
							<p className="dsv-promote__hint" style={{ marginTop: 8 }}>
								After saving, use the + button on this field in the schema table to add child fields.
							</p>
						)}

						{error && (
							<p
								className="dsv-modal__save-error"
								data-testid="dsv-field-modal-error"
								style={{ color: '#dc2626', fontSize: 13, margin: '8px 0 0' }}
							>
								{error}
							</p>
						)}
						<div className="dsv-modal__actions">
							<button className="dsv-btn dsv-btn--ghost" onClick={hide}>Cancel</button>
							<button className="dsv-btn dsv-btn--primary" onClick={handleSubmit} disabled={!hasChanges}>
								{isEdit ? 'Save Changes' : 'Add Field'}
							</button>
						</div>
					</CardConfigure.Body.Col>
				</CardConfigure.Body>
			</CardConfigure>
		</Modal>
	);
}

FieldModal.propTypes = { visible: PropTypes.bool, hide: PropTypes.func, field: PropTypes.object, onSave: PropTypes.func, parentFieldId: PropTypes.string, currentDepth: PropTypes.number };

// ============ Confirm Version Modal ============

const diffCellStyle = { padding: '8px 12px', border: '1px solid #e5e7eb', verticalAlign: 'top', wordBreak: 'break-word' };
const diffThStyle = { ...diffCellStyle, color: '#6b7280', fontWeight: 600, backgroundColor: '#f9fafb', textAlign: 'left' };
const diffTableStyle = { width: '100%', marginTop: 8, fontSize: 13, borderCollapse: 'collapse', tableLayout: 'fixed' };

function ConfirmVersionModal({ visible, hide, versionInfo, schemaDiff, payloadPreview, schemaPreview, ruleDiff, onConfirm }) {
	const [previewTab, setPreviewTab] = useState('payload');
	if (!versionInfo) return null;

	const { elementName, currentVersion, newVersion, changeType, changeDescription } = versionInfo;

	const { added: addedFields, modified: modifiedFields, removed: deletedFields } = schemaDiff;
	const hasChanges = addedFields.length > 0 || modifiedFields.length > 0 || deletedFields.length > 0;

	const addedRules = ruleDiff?.added || [];
	const modifiedRules = ruleDiff?.modified || [];
	const removedRules = ruleDiff?.removed || [];
	const hasRuleChanges = addedRules.length > 0 || modifiedRules.length > 0 || removedRules.length > 0;

	return (
		<DSModal visible={visible} close={hide} variant="large" dismissClickOutsideModal={false} addClass="dsv-confirm-modal">
			<DSCardConfigure>
				<DSCardConfigure.Header>
					<DSCardConfigure.Header.Title>Confirm New Version</DSCardConfigure.Header.Title>
					<DSCardConfigure.Header.Close>
						<button className="dsv-icon-btn" onClick={hide} title="Close">
							<IconClose width={16} height={16} />
						</button>
					</DSCardConfigure.Header.Close>
				</DSCardConfigure.Header>
				<DSCardConfigure.Body>
					<DSCardConfigure.Body.Col>
						<div className="dsv-confirm__layout">
						<div className="dsv-confirm__pane">
						<p className="dsv-confirm__subtitle">Please review the details before creating this version.</p>

						<div className="dsv-confirm__section">
							<h3 className="dsv-confirm__section-title">Version Details</h3>
							<div className="dsv-confirm__details">
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Data Element</span>
									<span className="dsv-confirm__value">{elementName}</span>
								</div>
								{changeType !== 'INITIAL' && (
									<div className="dsv-confirm__row">
										<span className="dsv-confirm__label">Base Version</span>
										<span className="dsv-confirm__value">v{currentVersion}</span>
									</div>
								)}
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">New Version</span>
									<span className="dsv-confirm__value dsv-confirm__value--highlight">v{newVersion}</span>
								</div>
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Change Type</span>
									<span className="dsv-confirm__value">{changeType}</span>
								</div>
								<div className="dsv-confirm__row">
									<span className="dsv-confirm__label">Status</span>
									<span className="dsv-confirm__value">DRAFT</span>
								</div>
								{changeDescription && (
									<div className="dsv-confirm__row">
										<span className="dsv-confirm__label">Description</span>
										<span className="dsv-confirm__value">{changeDescription}</span>
									</div>
								)}
							</div>
						</div>

						<div className="dsv-confirm__section">
							<h3 className="dsv-confirm__section-title">Schema Changes</h3>

							{/* ---------- Field changes sub-section ---------- */}
							<div className="dsv-confirm__subsection" data-testid="dsv-confirm-field-diff">
								<h4 className="dsv-confirm__subsection-title">Field Changes</h4>
								{hasChanges ? (
									<div className="dsv-confirm__changes">
										{addedFields.length > 0 && (
											<>
												<div className="dsv-confirm__change-group">
													<span className="dsv-confirm__change-label dsv-confirm__change-label--added">
														+{addedFields.length} added
													</span>
												</div>
												<div className="dsv-confirm__table-wrap">
												<table style={diffTableStyle}>
													<thead>
														<tr>
															{['Schema Key', 'Business Term', 'Data Type', 'Mandatory'].map(h => (
																<th key={h} style={diffThStyle}>{h}</th>
															))}
														</tr>
													</thead>
													<tbody>
														{addedFields.map(f => (
															<tr key={f.fieldId}>
																<td style={{ ...diffCellStyle, fontWeight: 600, color: '#15803d' }}>{f.displayPath || f.dataElementName}</td>
																<td style={diffCellStyle}>{f.businessTerm}</td>
																<td style={diffCellStyle}>{formatDataType(f)}</td>
																<td style={diffCellStyle}>{f.mandatory ? 'Yes' : 'No'}</td>
															</tr>
														))}
													</tbody>
												</table>
												</div>
											</>
										)}
										{modifiedFields.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={addedFields.length > 0 ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--modified">
														~{modifiedFields.length} modified
													</span>
												</div>
												<div className="dsv-confirm__table-wrap">
												<table style={diffTableStyle}>
													<thead>
														<tr>
															{['Field', 'Property', 'Before', 'After'].map(h => (
																<th key={h} style={diffThStyle}>{h}</th>
															))}
														</tr>
													</thead>
													<tbody>
														{modifiedFields.map(({ field, changes }) =>
															changes.map((change, i) => (
																<tr key={`${field.fieldId}-${change.property}`}>
																	{i === 0 && (
																		<td rowSpan={changes.length} style={{ ...diffCellStyle, fontWeight: 600 }}>
																			{field.displayPath || field.dataElementName}
																		</td>
																	)}
																	<td style={diffCellStyle}>{change.property}</td>
																	<td style={{ ...diffCellStyle, color: '#dc2626', backgroundColor: '#fef2f2', textDecoration: 'line-through' }}>{change.before}</td>
																	<td style={{ ...diffCellStyle, color: '#15803d', backgroundColor: '#f0fdf4' }}>{change.after}</td>
																</tr>
															))
														)}
													</tbody>
												</table>
												</div>
											</>
										)}
										{deletedFields.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={addedFields.length > 0 || modifiedFields.length > 0 ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--deleted">
														-{deletedFields.length} removed
													</span>
												</div>
												<div className="dsv-confirm__table-wrap">
												<table style={diffTableStyle}>
													<thead>
														<tr>
															{['Schema Key', 'Data Type'].map(h => (
																<th key={h} style={diffThStyle}>{h}</th>
															))}
														</tr>
													</thead>
													<tbody>
														{deletedFields.map(f => (
															<tr key={f.displayPath || f.dataElementName}>
																<td style={{ ...diffCellStyle, color: '#dc2626', textDecoration: 'line-through' }}>{f.displayPath || f.dataElementName}</td>
																<td style={{ ...diffCellStyle, textDecoration: 'line-through' }}>{formatDataType(f) || '-'}</td>
															</tr>
														))}
													</tbody>
												</table>
												</div>
											</>
										)}
									</div>
								) : (
									<p className="dsv-confirm__empty">No fields were added, modified, or removed.</p>
								)}
							</div>

							{/* ---------- Conditional rule changes sub-section ---------- */}
							<div className="dsv-confirm__subsection" data-testid="dsv-confirm-rule-diff" style={{ marginTop: 20 }}>
								<h4 className="dsv-confirm__subsection-title">Conditional Rule Changes (x-allOf)</h4>
								{hasRuleChanges ? (
									<>
										{addedRules.length > 0 && (
											<>
												<div className="dsv-confirm__change-group">
													<span className="dsv-confirm__change-label dsv-confirm__change-label--added">
														+{addedRules.length} added
													</span>
												</div>
												<div className="dsv-confirm__rule-list">
													{addedRules.map((rule) => (
														<div key={rule.id} className="dsv-confirm__rule-card" data-testid="dsv-confirm-rule-added">
															<p className="dsv-confirm__rule-summary dsv-confirm__rule-summary--added">
																{summarizeRule(rule)}
															</p>
															<JsonViewer data={emitRule(rule)} />
														</div>
													))}
												</div>
											</>
										)}
										{modifiedRules.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={addedRules.length > 0 ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--modified">
														~{modifiedRules.length} modified
													</span>
												</div>
												<div className="dsv-confirm__rule-list">
													{modifiedRules.map(({ baseRule, rule }) => (
														<div key={rule.id} className="dsv-confirm__rule-card" data-testid="dsv-confirm-rule-modified">
															<p className="dsv-confirm__rule-summary dsv-confirm__rule-summary--modified">
																{summarizeRule(rule)}
															</p>
															<div className="dsv-confirm__rule-before-after">
																<div>
																	<p className="dsv-confirm__rule-ba-label">Before</p>
																	<JsonViewer data={emitRule(baseRule)} />
																</div>
																<div>
																	<p className="dsv-confirm__rule-ba-label">After</p>
																	<JsonViewer data={emitRule(rule)} />
																</div>
															</div>
														</div>
													))}
												</div>
											</>
										)}
										{removedRules.length > 0 && (
											<>
												<div className="dsv-confirm__change-group" style={(addedRules.length > 0 || modifiedRules.length > 0) ? { marginTop: 16 } : undefined}>
													<span className="dsv-confirm__change-label dsv-confirm__change-label--deleted">
														-{removedRules.length} removed
													</span>
												</div>
												<div className="dsv-confirm__rule-list">
													{removedRules.map((rule) => (
														<div key={rule.id} className="dsv-confirm__rule-card" data-testid="dsv-confirm-rule-removed">
															<p className="dsv-confirm__rule-summary dsv-confirm__rule-summary--removed">
																{summarizeRule(rule)}
															</p>
															<JsonViewer data={emitRule(rule)} />
														</div>
													))}
												</div>
											</>
										)}
									</>
								) : (
									<p className="dsv-confirm__empty">No conditional rules were added, modified, or removed.</p>
								)}
							</div>
						</div>
						</div>
						<div className="dsv-confirm__pane dsv-confirm__pane--preview">
							<div className="dsv-update__tabs dsv-confirm__preview-tabs">
								<div
									className={`dsv-update__tab ${previewTab === 'payload' ? 'dsv-update__tab--active' : ''}`}
									onClick={() => setPreviewTab('payload')}
									data-testid="dsv-confirm-tab-payload"
								>Payload Preview</div>
								<div
									className={`dsv-update__tab ${previewTab === 'schema' ? 'dsv-update__tab--active' : ''}`}
									onClick={() => setPreviewTab('schema')}
									data-testid="dsv-confirm-tab-schema"
								>Schema Preview</div>
							</div>
							<div className="dsv-confirm__preview">
								{previewTab === 'payload'
									? <JsonViewer data={payloadPreview} />
									: <JsonViewer data={schemaPreview} />}
							</div>
						</div>
						</div>
					</DSCardConfigure.Body.Col>
				</DSCardConfigure.Body>
				<DSCardConfigure.Footer>
					<DSCardConfigure.Footer.Left />
					<DSCardConfigure.Footer.Right>
						<button className="dsv-btn dsv-btn--ghost" onClick={hide}>Cancel</button>
						<button className="dsv-btn dsv-btn--primary" style={{ marginLeft: rem(12) }} onClick={onConfirm}>Create Version</button>
					</DSCardConfigure.Footer.Right>
				</DSCardConfigure.Footer>
			</DSCardConfigure>
		</DSModal>
	);
}

ConfirmVersionModal.propTypes = {
	visible: PropTypes.bool,
	hide: PropTypes.func,
	versionInfo: PropTypes.object,
	schemaDiff: PropTypes.object,
	payloadPreview: PropTypes.object,
	schemaPreview: PropTypes.object,
	ruleDiff: PropTypes.object,
	onConfirm: PropTypes.func,
};

// ============ Input Sanitization ============

function sanitizeText(value) {
	if (typeof value !== 'string') return '';
	return value.replace(/<[^>]*>/g, '').trim();
}

export function toDataElementId(value) {
	let id = '';
	let prevWasSep = true;
	for (let i = 0; i < value.length; i++) {
		const c = value.charCodeAt(i);
		const isLower = c >= 97 && c <= 122;
		const isUpper = c >= 65 && c <= 90;
		const isDigit = c >= 48 && c <= 57;
		if (isLower || isDigit) {
			id += value[i];
			prevWasSep = false;
		} else if (isUpper) {
			id += String.fromCharCode(c + 32);
			prevWasSep = false;
		} else if (!prevWasSep) {
			id += '_';
			prevWasSep = true;
		}
	}
	if (id.endsWith('_')) id = id.slice(0, -1);
	return id;
}

// ============ Main Page Component ============

const DSVUpdateSchemaPage = () => {
	const { goTo } = useNavigate();
	const { dataElementId } = useParams();
	// useToast is per-component state, not a shared singleton; render the
	// matching <Toast> locally so addToastMessage calls from this page
	// actually surface (the Layout-level Toast is driven by its own
	// isolated state and does not render messages raised here).
	const { toastList, addToastMessage } = useToast();
	const user = useSelector((state) => state.auth.user);
	const canEditSchema = user?.role?.some((role) =>
		role.name === 'SGTradex Admin' || role.name === 'Super SGTradex Admin'
	);

	const {
		loading,
		error,
		schemaFields,
		setSchemaFields,
		selectedVersion,
		fetchSingleVersion,
		fetchAllVersions,
		currentVersionDetail,
		createNewVersion,
		createInitialVersions,
		promoteVersion,
		patchVersion,
		createDataElement,
		conflictError,
		setConflictError,
		impactAnalysis,
		fetchImpactAnalysis,
	} = useDataSchemaVersioning();

	// Local state
	const [selectedTab, setSelectedTab] = useState('edit');
	const [changeType, setChangeType] = useState('PATCH');
	const [changeDescription, setChangeDescription] = useState('');
	const [newVersion, setNewVersion] = useState('');
	const [editingField, setEditingField] = useState(null);
	const [editingParentFieldId, setEditingParentFieldId] = useState(null);

	// Track local changes
	const [localFields, setLocalFields] = useState([]);

	// Inline validation errors
	const [formErrors, setFormErrors] = useState({});

	// Schema validation errors (ajv)
	const [schemaValidationErrors, setSchemaValidationErrors] = useState([]);

	// x-allOf conditional rules
	const [xAllOfRules, setXAllOfRules] = useState([]);

	// Root-level title / description captured from the last uploaded JSON
	// schema. The editor has no UI surface for these, so we stash them so
	// they round-trip into the Schema Preview + save payload (GAP-1/GAP-2).
	const [uploadedRootMeta, setUploadedRootMeta] = useState(null);

	// x-allOf rule-level validation errors (from XAllOfBuilder). Non-empty
	// blocks save. Shape: { [ruleId]: { ifConditions?, thenFields?, ... } }
	const [xAllOfErrors, setXAllOfErrors] = useState({});

	// Track which version id's schema we already hydrated from, so refetches
	// of the same version don't clobber unsaved rule edits.
	const lastLoadedVersionIdRef = useRef(null);

	// Autosave: track updatedAt from server for optimistic locking
	const [serverUpdatedAt, setServerUpdatedAt] = useState(null);

	// Create DE mode: new data element name
	const [newDataElementName, setNewDataElementName] = useState('');

	// Autosave debounce ref
	const autosaveTimerRef = useRef(null);

	// Modals (order matters for test mocking: field=0, confirm=1, promote=2, conflict=3)
	const { visible: visibleFieldModal, show: showFieldModal, hide: hideFieldModal } = useModal();
	const { visible: visibleConfirmModal, show: showConfirmModal, hide: hideConfirmModal } = useModal();
	const { visible: visiblePromoteModal, show: showPromoteModal, hide: hidePromoteModal } = useModal();
	const { visible: visibleConflictModal, show: showConflictModal, hide: hideConflictModal } = useModal();

	const elementName = selectedVersion?.elementName || currentVersionDetail?.elementName || 'Data Element';
	const currentVersion = selectedVersion?.version || currentVersionDetail?.version || '1.0.0';
	const versionStatus = selectedVersion?.status || currentVersionDetail?.status;
	const isCreateMode = new URLSearchParams(window.location.search).get('mode') === 'create';
	const isEditable = canEditSchema && (isCreateMode || versionStatus === 'DRAFT');
	// Pure create mode = brand-new DE (no base version). Distinguished from
	// "create new version of existing DE" which also has isCreateMode=true but
	// hydrates selectedVersion from ?version= param.
	const isPureCreateMode = isCreateMode && !selectedVersion && !currentVersionDetail;
	const schemaLabel = isPureCreateMode
		? 'Create Data Element'
		: (isEditable ? 'Update Schema' : 'View Schema');
	const pageTitle = isPureCreateMode ? schemaLabel : `${schemaLabel}: ${elementName}`;

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{ label: '', link: '/' },
			{ label: 'Data Element', link: '/dsv' },
			{ label: elementName, link: `/dsv/${dataElementId}` },
			{ label: schemaLabel, link: '' },
		],
	});

	// ============ Fetch Data ============

	useEffect(() => {
		if (dataElementId) {
			const searchParams = new URLSearchParams(window.location.search);
			const version = searchParams.get('version');
			if (version) {
				fetchSingleVersion(dataElementId, version);
			} else {
				// Load all versions and pick the latest
				fetchAllVersions(dataElementId);
			}
		}
	}, [dataElementId]);

	// Sync schemaFields from hook into localFields
	useEffect(() => {
		if (schemaFields && schemaFields.length > 0) {
			setLocalFields(schemaFields.map(f => ({ ...f })));
		}
	}, [schemaFields]);

	// Set defaults from selected version — but only when the version identity
	// actually changes. Refetches of the same version (e.g. after a 200
	// autosave response) used to clobber unsaved rule edits; we gate on a ref
	// to avoid that.
	useEffect(() => {
		if (!selectedVersion) return;
		const versionKey = selectedVersion.id
			|| `${selectedVersion.dataElementId}@${selectedVersion.version}`;
		if (lastLoadedVersionIdRef.current === versionKey) return;
		lastLoadedVersionIdRef.current = versionKey;

		const schema = selectedVersion.elementSchema || selectedVersion.elementSchemaJson;
		if (schema && localFields.length === 0) {
			setLocalFields(extractSchemaFields(schema));
		}
		if (schema?.['x-allOf']) {
			setXAllOfRules(xAllOfToRules(schema['x-allOf']));
		} else {
			setXAllOfRules([]);
		}
	}, [selectedVersion]);

	// Track updatedAt from fetched version
	useEffect(() => {
		const version = selectedVersion || currentVersionDetail;
		if (version?.updatedAt) {
			setServerUpdatedAt(version.updatedAt);
		}
	}, [selectedVersion, currentVersionDetail]);

	// Fetch impact analysis when we have version info
	useEffect(() => {
		if (dataElementId && currentVersion) {
			fetchImpactAnalysis(dataElementId, currentVersion);
		}
	}, [dataElementId, currentVersion]);

	// Show conflict modal when conflict error is detected
	useEffect(() => {
		if (conflictError) {
			showConflictModal();
		}
	}, [conflictError]);

	// Overlay captured root `title` / `description` from the last JSON
	// upload so they round-trip into the preview and save payload (GAP-1/2).
	// The editor has no form surface for these fields.
	function applyUploadedRootMeta(schema) {
		if (!uploadedRootMeta || !schema) return schema;
		const out = { ...schema };
		if (uploadedRootMeta.title != null) out.title = uploadedRootMeta.title;
		if (uploadedRootMeta.description != null) out.description = uploadedRootMeta.description;
		if (Array.isArray(uploadedRootMeta.allOf) && uploadedRootMeta.allOf.length > 0) {
			out.allOf = uploadedRootMeta.allOf;
		}
		return out;
	}

	// Autosave: debounced PATCH when localFields change (DRAFT only).
	const AUTOSAVE_ENABLED = true;
	const performAutosave = useCallback(async () => {
		if (!AUTOSAVE_ENABLED) return;
		if (!isEditable || versionStatus !== 'DRAFT' || !dataElementId || isCreateMode) return;

		const baseSchema = selectedVersion?.elementSchema || selectedVersion?.elementSchemaJson;
		const currentSchema = applyUploadedRootMeta(buildJsonSchema(
			localFields.filter(f => f.status !== 'Deleted'),
			baseSchema,
			rulesToXAllOf(xAllOfRules)
		));

		const payload = {
			schema: currentSchema,
			updatedAt: serverUpdatedAt,
		};

		const result = await patchVersion(dataElementId, currentVersion, payload);
		if (result && !result.error) {
			if (result.updatedAt) {
				setServerUpdatedAt(result.updatedAt);
			}
		} else if (result?.conflict) {
			// Conflict modal will show via the effect above
		}
	}, [localFields, serverUpdatedAt, dataElementId, currentVersion, versionStatus, isEditable, isCreateMode, selectedVersion, xAllOfRules]);

	// Trigger autosave on localFields change with 2s debounce
	useEffect(() => {
		if (!AUTOSAVE_ENABLED) return;
		if (!isEditable || versionStatus !== 'DRAFT' || isCreateMode) return;
		if (autosaveTimerRef.current) {
			clearTimeout(autosaveTimerRef.current);
		}
		autosaveTimerRef.current = setTimeout(() => {
			performAutosave();
		}, 2000);
		return () => {
			if (autosaveTimerRef.current) {
				clearTimeout(autosaveTimerRef.current);
			}
		};
	}, [localFields, performAutosave]);

	// ============ Handlers ============

	function handleBackToElement() {
		goTo(`/dsv/${dataElementId}`);
	}

	function handleJsonUpload(parsedSchema) {
		if (!parsedSchema || typeof parsedSchema !== 'object') {
			addToastMessage('Uploaded file does not contain a valid schema object', 'error');
			return;
		}
		const fields = extractSchemaFields(parsedSchema);
		if (fields.length === 0) {
			addToastMessage('Uploaded schema has no properties to extract', 'error');
			return;
		}
		setLocalFields(fields.map(f => ({ ...f, status: 'New' })));

		// Capture root-level title / description / allOf so buildJsonSchema
		// can re-emit them even though the editor has no form surface for
		// them. Standard `allOf` (not `x-allOf`) flows through as-is —
		// admin-ui's builder only understands x-allOf, but silently dropping
		// a user-authored allOf would be data loss.
		setUploadedRootMeta({
			title: typeof parsedSchema.title === 'string' ? parsedSchema.title : null,
			description: typeof parsedSchema.description === 'string' ? parsedSchema.description : null,
			allOf: Array.isArray(parsedSchema.allOf) ? parsedSchema.allOf : null,
		});

		// Parse any `x-allOf` rules in the uploaded schema so they show up in
		// the Conditional Validation Rules builder and in the Schema Preview.
		const uploadedRules = Array.isArray(parsedSchema['x-allOf'])
			? xAllOfToRules(parsedSchema['x-allOf'])
			: [];
		setXAllOfRules(uploadedRules);

		const ruleNote = uploadedRules.length > 0
			? ` and ${uploadedRules.length} conditional rule${uploadedRules.length === 1 ? '' : 's'}`
			: '';
		addToastMessage(`Schema fields${ruleNote} populated from uploaded JSON`, 'success');
	}

	function handleConflictReload() {
		hideConflictModal();
		setConflictError(null);
		const searchParams = new URLSearchParams(window.location.search);
		const version = searchParams.get('version');
		if (version) {
			fetchSingleVersion(dataElementId, version);
		} else {
			fetchAllVersions(dataElementId);
		}
	}

	function handleAddField() {
		setEditingField(null);
		setEditingParentFieldId(null);
		showFieldModal();
	}

	function handleAddChildField(parentField) {
		const depth = getFieldDepth(parentField.fieldId) + 1;
		if (depth >= MAX_NESTING_DEPTH) {
			addToastMessage(`Maximum nesting depth (${MAX_NESTING_DEPTH} levels) reached`, 'error');
			return;
		}
		setEditingField(null);
		setEditingParentFieldId(parentField.fieldId);
		showFieldModal();
	}

	function handleEditField(field) {
		setEditingField(field);
		setEditingParentFieldId(field.parentKey || null);
		showFieldModal();
	}

	function handleDeleteField(field) {
		if (window.confirm(`Are you sure you want to delete "${field.dataElementName}"?`)) {
			if (field.status === 'New') {
				// New fields added this session — remove entirely
				setLocalFields(prev => removeFieldFromTree(prev, field.fieldId));
			} else {
				// Existing fields — mark as deleted (strikethrough)
				setLocalFields(prev => updateFieldInTree(prev, field.fieldId, f => ({ ...f, status: 'Deleted' })));
			}
		}
	}

	function handleSaveField(formData) {
		const isPrimitiveType = formData.dataType !== 'object' && formData.dataType !== 'array';
		const parsedExampleValue = isPrimitiveType
			? parseExampleValue(formData.exampleValue, formData.dataType)
			: { value: '' };
		if (parsedExampleValue.error) {
			return parsedExampleValue.error;
		}

		const sanitized = {
			...formData,
			dataElementName: sanitizeText(formData.dataElementName),
			businessTerm: sanitizeText(formData.businessTerm),
			exampleValue: parsedExampleValue.value,
		};

		if (!sanitized.dataElementName || !sanitized.businessTerm) {
			return 'Schema Key and Business Term are required';
		}

		const parentId = sanitized.parentFieldId || editingParentFieldId;

		// Check for duplicate technical field name among siblings
		const siblings = parentId
			? (function findChildren(fields, pid) {
				for (const f of fields) {
					if (f.fieldId === pid) return f.children || [];
					if (f.children?.length > 0) { const found = findChildren(f.children, pid); if (found) return found; }
				}
				return null;
			})(localFields, parentId) || []
			: localFields;
		const duplicateKey = siblings.some(f =>
			f.fieldId !== sanitized.fieldId &&
			f.status !== 'Deleted' &&
			f.dataElementName === sanitized.dataElementName
		);
		if (duplicateKey) {
			return `Duplicate key: "${sanitized.dataElementName}" already exists at this level`;
		}

		if (sanitized.fieldId) {
			// Edit existing field at any depth, preserving children
			setLocalFields(prev => updateFieldInTree(prev, sanitized.fieldId, f => ({
				...sanitized,
				children: f.children || [],
				parentKey: f.parentKey,
				status: 'Modified',
			})));
		} else if (parentId) {
			// Add new child field
			const childFieldId = `${parentId}.${sanitized.dataElementName}`;
			const newChild = {
				...sanitized,
				fieldId: childFieldId,
				parentKey: parentId,
				children: [],
				status: 'New',
			};
			setLocalFields(prev => addChildToField(prev, parentId, newChild));
		} else {
			// Add new root field — use technical field name as fieldId for readable child paths
			const newField = {
				...sanitized,
				fieldId: sanitized.dataElementName || `new-${Date.now()}`,
				parentKey: null,
				children: [],
				status: 'New',
			};
			setLocalFields(prev => [...prev, newField]);
		}

		setEditingParentFieldId(null);
	}

	/**
	 * Show confirmation modal before creating version.
	 * Validates required fields first, then shows the modal for review.
	 */
	function handleConfirmChanges() {
		const semverRegex = /^\d+\.\d+\.\d+$/;
		const errors = {};

		// Pure create skips version/semver validation — backend hard-codes v1.0.0 INITIAL.
		if (!isPureCreateMode) {
			if (!newVersion) {
				errors.newVersion = 'This field is required';
			} else if (!semverRegex.test(newVersion)) {
				errors.newVersion = 'Must be in semver format (e.g. 1.0.1)';
			} else {
				// Validate version matches change type relative to base
				const [baseMajor, baseMinor, basePatch] = currentVersion.split('.').map(Number);
				const [newMajor, newMinor, newPatch] = newVersion.split('.').map(Number);

				const nextPatch = `${baseMajor}.${baseMinor}.${basePatch + 1}`;
				const nextMinor = `${baseMajor}.${baseMinor + 1}.0`;
				const nextMajor = `${baseMajor + 1}.0.0`;

				if (changeType === 'PATCH') {
					if (newMajor !== baseMajor || newMinor !== baseMinor || newPatch <= basePatch) {
						errors.newVersion = `Next patch version should be ${nextPatch}. From v${currentVersion} → v${nextPatch}`;
					}
				} else if (changeType === 'MINOR') {
					if (newMajor !== baseMajor || newMinor <= baseMinor || newPatch !== 0) {
						errors.newVersion = `Next minor version should be ${nextMinor}. From v${currentVersion} → v${nextMinor}`;
					}
				} else if (changeType === 'MAJOR') {
					if (newMajor <= baseMajor || newMinor !== 0 || newPatch !== 0) {
						errors.newVersion = `Next major version should be ${nextMajor}. From v${currentVersion} → v${nextMajor}`;
					}
				}
			}
		}

		if (isPureCreateMode && !newDataElementName.trim()) {
			errors.newDataElementName = 'Data Element Name is required';
		}

		if (!changeDescription.trim()) {
			errors.changeDescription = 'This field is required';
		}

		setFormErrors(errors);

		if (Object.keys(errors).length > 0) {
			return;
		}

		// Block save when any x-allOf rule has validation errors (empty
		// ifConditions, dangling refs, invalid regex, bad bounds, duplicates).
		// XAllOfBuilder's useRuleValidation hook keeps xAllOfErrors in sync.
		if (xAllOfErrors && Object.keys(xAllOfErrors).length > 0) {
			addToastMessage(
				'Conditional rules have errors. Fix them before saving.',
				'error',
			);
			return;
		}

		// Validate schema shape with ajv before allowing confirm
		const baseSchema = isCreateMode
			? undefined
			: (selectedVersion?.elementSchema || selectedVersion?.elementSchemaJson);
		const currentSchema = applyUploadedRootMeta(buildJsonSchema(
			localFields.filter(f => f.status !== 'Deleted'),
			baseSchema,
			rulesToXAllOf(xAllOfRules)
		));
		const shapeResult = validateSchemaShape(currentSchema);
		if (!shapeResult.valid) {
			setSchemaValidationErrors(shapeResult.errors);
			addToastMessage('Schema has structural errors. Please fix them before saving.', 'error');
			return;
		}
		setSchemaValidationErrors([]);

		// Pure-create only: the generated querySchema must have at least one required
		// scalar — it becomes the mandatory pull-side query key. Without it, downstream
		// pulls cannot be satisfied (pitstop-core rejects empty query params).
		if (isPureCreateMode) {
			const querySchemaPreview = buildQuerySchemaFromElementSchema(currentSchema);
			if (!Array.isArray(querySchemaPreview.required) || querySchemaPreview.required.length === 0) {
				addToastMessage(
					'At least one required scalar field (string / number / integer / boolean) must be defined. It becomes the mandatory query key for pulling this data element.',
					'error'
				);
				return;
			}
		}

		// Sanitize text inputs before showing confirmation
		setChangeDescription(sanitizeText(changeDescription));
		setNewVersion(newVersion.trim());

		showConfirmModal();
	}

	/**
	 * Actually creates the new version after user confirms in the modal.
	 * POST /data-element-versions/:dataElementId/versions
	 */
	async function handleSubmitVersion() {
		hideConfirmModal();

		const baseSchema = selectedVersion?.elementSchema || selectedVersion?.elementSchemaJson;
		const newSchema = applyUploadedRootMeta(buildJsonSchema(
			localFields.filter(f => f.status !== 'Deleted'),
			baseSchema,
			rulesToXAllOf(xAllOfRules)
		));

		if (isPureCreateMode) {
			const trimmedName = (newDataElementName || '').trim();
			if (!trimmedName) {
				addToastMessage('Data Element Name is required', 'error');
				return;
			}
			const newId = toDataElementId(trimmedName);
			if (!newId) {
				addToastMessage('Data Element Name must contain alphanumeric characters', 'error');
				return;
			}
			const createPayload = [{
				id: newId,
				name: trimmedName,
				schema: newSchema,
				querySchema: buildQuerySchemaFromElementSchema(newSchema),
				version: '1.0.0',
				changeType: 'INITIAL',
				changeDescription: changeDescription,
				status: 'DRAFT',
				type: 'DOCUMENT',
			}];

			const result = await createInitialVersions(createPayload);
			if (result?.error) {
				addToastMessage(typeof result.error === 'string' ? result.error : 'Failed to create data element', 'error');
				return;
			}
			addToastMessage('Data Element created successfully', 'success');
			goTo(`/dsv/${newId}`);
			return;
		}

		const payload = {
			name: elementName,
			schema: newSchema,
			querySchema: selectedVersion?.querySchema || currentVersionDetail?.querySchema || undefined,
			version: newVersion,
			changeType: changeType,
			changeDescription: changeDescription,
			status: 'DRAFT',
			type: selectedVersion?.type || currentVersionDetail?.type || 'DOCUMENT',
		};

		const result = await createNewVersion(dataElementId, payload);

		if (result?.error) {
			addToastMessage(typeof result.error === 'string' ? result.error : 'Failed to create version', 'error');
			return;
		}

		addToastMessage('Version created successfully', 'success');
		goTo(`/dsv/${dataElementId}`);
	}

	// ============ Promote Handlers ============

	const isVersionDataLoaded = !!(selectedVersion || currentVersionDetail);
	const isPageReady = isCreateMode ? !loading : (!loading && isVersionDataLoaded);
	const transitionConfig = !isCreateMode ? getTransitionConfig(versionStatus) : null;
	const disabledReason = transitionConfig && selectedVersion ? transitionConfig.getDisabledReason(selectedVersion) : null;
	const enabledHint = transitionConfig && selectedVersion ? transitionConfig.getEnabledHint(selectedVersion) : null;

	async function handleConfirmPromote(targetStatus, expiryDate) {
		hidePromoteModal();
		const config = getTransitionConfig(versionStatus);
		const result = await promoteVersion(dataElementId, currentVersion, targetStatus, expiryDate);
		if (result?.error) {
			addToastMessage(
				typeof result.error === 'string' ? result.error : `Failed to promote v${currentVersion}`,
				'error'
			);
		} else {
			addToastMessage(`Version ${currentVersion} ${config?.verb || 'promoted'}`, 'success');
			goTo(`/dsv/${dataElementId}`);
		}
	}

	const tabs = [
		{ key: 'edit', label: isEditable ? 'Edit Schema' : 'Schema' },
		{ key: 'json', label: 'Payload Preview' },
		{ key: 'schema', label: 'Schema Preview' },
		{ key: 'validate', label: 'Validate' },
		...(isEditable ? [
			{ key: 'changes', label: 'View Changes' },
			{ key: 'impact', label: 'Impact Analysis' },
		] : []),
	];

	const visibleFields = localFields.filter(f => f.status !== 'Deleted');

	// Resolve the baseline schema once. Only pure create (brand-new DE) has
	// no base to diff against — "create new version of existing DE"
	// (`?mode=create&version=X`) hydrates selectedVersion and SHOULD diff
	// against it, otherwise the Confirm modal treats every field as "added"
	// and never shows removals/modifications.
	const baseSchemaForDiff = isPureCreateMode
		? undefined
		: (selectedVersion?.elementSchema || selectedVersion?.elementSchemaJson);

	// Single source of truth for the emitted JSON Schema: reused by
	// JsonDownload, the Schema Preview tab, and the confirm modal.
	const finalSchema = useMemo(
		() => applyUploadedRootMeta(buildJsonSchema(
			localFields.filter(f => f.status !== 'Deleted'),
			baseSchemaForDiff,
			rulesToXAllOf(xAllOfRules),
		)),
		[localFields, baseSchemaForDiff, xAllOfRules, uploadedRootMeta],
	);

	// Rule-level diff for the confirm modal + View Changes tab. In create mode
	// there is no baseline so every rule shows as "added" — which is the
	// correct user-facing framing.
	const ruleDiff = useMemo(
		() => diffXAllOfRules(xAllOfRules, baseSchemaForDiff),
		[xAllOfRules, baseSchemaForDiff],
	);
	// Collect all changed fields recursively with human-readable display path
	function collectChangedFields(fields, parentPath) {
		const result = [];
		for (const f of fields) {
			const displayPath = parentPath ? `${parentPath}.${f.dataElementName}` : f.dataElementName;
			if (f.status) result.push({ ...f, displayPath });
			if (f.children && f.children.length > 0) {
				result.push(...collectChangedFields(f.children, displayPath));
			}
		}
		return result;
	}
	const changedFields = collectChangedFields(localFields, '');
	const hasFormChanges = isPureCreateMode
		? (newDataElementName.trim() !== '' || changeDescription.trim() !== '' || changedFields.length > 0)
		: (newVersion.trim() !== '' || changeDescription.trim() !== '' || changedFields.length > 0);


	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			{/* Back link */}
			<div className="dsv-detail__back" onClick={handleBackToElement}>
				<span className="dsv-detail__back-arrow">&larr;</span>
				<span>Back to Element</span>
			</div>

			{/* Create DE Name Input */}
			{isPureCreateMode && isEditable && (
				<div className="dsv-update__create-de" style={{ marginBottom: 16 }}>
					<label className="dsv-update__version-label">Data Element Name</label>
					<input
						className={`dsv-modal__input ${formErrors.newDataElementName ? 'dsv-modal__input--error' : ''}`}
						type="text"
						maxLength={200}
						value={newDataElementName}
						onChange={(e) => {
							setNewDataElementName(e.target.value);
							setFormErrors(prev => ({ ...prev, newDataElementName: undefined }));
						}}
						placeholder="Enter new data element name"
						data-testid="create-de-name-input"
					/>
					{formErrors.newDataElementName && (
						<span className="dsv-form-error">{formErrors.newDataElementName}</span>
					)}
				</div>
			)}

			{/* Header */}
			<div className="dsv-update__header">
				<div className="dsv-update__header-left">
					<h1 className="dsv-update__title">{pageTitle}</h1>
					<div className="dsv-update__subtitle">
						{isPureCreateMode
							? 'Define fields and conditional rules for the new data element. The first version will be v1.0.0.'
							: isEditable
								? `Creating new version based on v${currentVersion}`
								: `Viewing v${currentVersion}`}
						{versionStatus && versionStatus !== 'DRAFT' && (
							<span style={{ marginLeft: 8 }}><StatusBadge status={versionStatus} /></span>
						)}
					</div>
				</div>
				<div className="dsv-update__header-right">
					{isEditable && (
						<>
							<JsonUpload onUpload={handleJsonUpload} />
							<JsonDownload
								schema={finalSchema}
								dataElementId={dataElementId}
								version={currentVersion}
							/>
						</>
					)}
					{isEditable && hasFormChanges && (
						<button className="dsv-btn dsv-btn--primary" onClick={handleConfirmChanges}>
							Confirm Changes
						</button>
					)}
					{canEditSchema && transitionConfig && !isCreateMode && isVersionDataLoaded && (
						<div style={{ textAlign: 'right' }} data-testid="dsv-promote-region">
							<button
								className={`dsv-btn ${transitionConfig.buttonClass}`}
								onClick={showPromoteModal}
								disabled={!!disabledReason}
								title={disabledReason || enabledHint || undefined}
							>
								{transitionConfig.buttonLabel}
							</button>
							{disabledReason && (
								<p className="dsv-promote__hint" style={{ marginTop: 4, fontSize: 12 }}>
									{disabledReason}
								</p>
							)}
						</div>
					)}
					{isPageReady && <span data-testid="dsv-page-ready" style={{ display: 'none' }} />}
				</div>
			</div>

			{/* Version Info Bar — hidden in pure create mode (version is always v1.0.0 INITIAL) */}
			{!isPureCreateMode && (
				<div className="dsv-update__version-bar">
					<div className="dsv-update__version-field">
						<label className="dsv-update__version-label">Current Version</label>
						<div className="dsv-update__version-value">
							v{currentVersion}
						</div>
					</div>
					{isEditable && (
						<>
							<div className="dsv-update__version-field">
								<label className="dsv-update__version-label">New Version</label>
								<input className={`dsv-modal__input ${formErrors.newVersion ? 'dsv-modal__input--error' : ''}`} type="text" value={newVersion}
									onChange={(e) => { setNewVersion(e.target.value); setFormErrors(prev => ({ ...prev, newVersion: undefined })); }} placeholder="e.g. 2.0.0" />
								{formErrors.newVersion && <span className="dsv-form-error">{formErrors.newVersion}</span>}
							</div>
							<div className="dsv-update__version-field">
								<label className="dsv-update__version-label">Change Type</label>
								<select className="dsv-modal__select" value={changeType}
									onChange={(e) => setChangeType(e.target.value)}>
									{CHANGE_TYPES.map((ct) => (
										<option key={ct.value} value={ct.value}>{ct.name}</option>
									))}
								</select>
							</div>
						</>
					)}
				</div>
			)}

			{isEditable && (
				<div className="dsv-update__description">
					<label className="dsv-update__version-label">
						{isPureCreateMode ? 'Description' : 'Change Description'}
					</label>
					<textarea className={`dsv-modal__textarea ${formErrors.changeDescription ? 'dsv-modal__textarea--error' : ''}`} value={changeDescription}
						onChange={(e) => { setChangeDescription(e.target.value); setFormErrors(prev => ({ ...prev, changeDescription: undefined })); }}
						placeholder={isPureCreateMode
							? 'Describe the purpose of this data element...'
							: 'Describe what changes are being made in this version...'} rows={3} />
					{formErrors.changeDescription && <span className="dsv-form-error">{formErrors.changeDescription}</span>}
				</div>
			)}

			{/* Tabs */}
			<div className="dsv-update__tabs">
				{tabs.map((tab) => (
					<div key={tab.key}
						className={`dsv-update__tab ${selectedTab === tab.key ? 'dsv-update__tab--active' : ''}`}
						onClick={() => setSelectedTab(tab.key)}>
						{tab.label}
					</div>
				))}
			</div>

			{/* Tab Content */}
			{selectedTab === 'edit' && (
				<>
					{isEditable && (
						<>
							<div className="dsv-schema__info-bar">
								<span className="dsv-schema__info-icon">&#9432;</span>
								<span>You are editing schema fields. Click on any field to modify its properties, or add new fields using the button below.</span>
							</div>
							<div className="dsv-schema__add-field-row">
								<button className="dsv-btn dsv-btn--primary" onClick={handleAddField}>+ Add Field</button>
							</div>
						</>
					)}
					<SchemaFieldsTable fields={localFields} onEditField={handleEditField} onDeleteField={handleDeleteField} onAddChild={handleAddChildField} canEditSchema={isEditable} />

					{isEditable && (
						<XAllOfBuilder
							fields={localFields}
							rules={xAllOfRules}
							onRulesChange={setXAllOfRules}
							onValidationChange={setXAllOfErrors}
						/>
					)}
				</>
			)}

			{selectedTab === 'json' && (
				<JsonViewer data={buildPayloadTemplate(
					localFields,
					isCreateMode ? undefined
						: (selectedVersion?.elementSchema || selectedVersion?.elementSchemaJson),
				)} />
			)}

			{selectedTab === 'schema' && (
				<div data-testid="dsv-schema-preview">
					<JsonViewer data={finalSchema} />
				</div>
			)}

			{/*
			 * ValidationPreview owns the pasted payload / result via useState.
			 * Render it always (hidden via CSS when inactive) so switching tabs
			 * doesn't unmount the component and wipe the user's draft.
			 */}
			<div style={{ display: selectedTab === 'validate' ? 'block' : 'none' }}>
				<ValidationPreview schema={finalSchema} />
			</div>

			{selectedTab === 'changes' && (
				<div className="dsv-update__changes">
					<div className="dsv-update__changes-list">
						{changedFields.length > 0 ? (
							changedFields.map((field) => {
								const activeChildren = (field.children || []).filter(c => c.status !== 'Deleted');
								const childInfo = (field.dataType === 'object' || field.dataType === 'array') && activeChildren.length > 0
									? ` (${activeChildren.length} child field${activeChildren.length > 1 ? 's' : ''})`
									: '';

								return (
									<div className="dsv-update__change-item" key={field.fieldId}>
										<FieldStatusBadge status={field.status} />
										<span className="dsv-update__change-name">
											{field.displayPath || field.dataElementName}
										</span>
										<span style={{ color: '#6b7280', fontSize: 12 }}>
											{field.dataType}{childInfo}
										</span>
										<span className={`dsv-mandatory-badge ${field.mandatory ? 'dsv-mandatory-badge--mandatory' : 'dsv-mandatory-badge--optional'}`} style={{ fontSize: 11 }}>
											{field.mandatory ? 'M' : 'O'}
										</span>
										<span className="dsv-update__change-desc">
											{field.status === 'New' ? 'New field added' :
												field.status === 'Modified' ? 'Field properties updated' :
													field.status === 'Deleted' ? 'Field removed' : ''}
										</span>
									</div>
								);
							})
						) : (
							<div className="dsv-update__changes-empty">No changes made yet.</div>
						)}
					</div>

					{(ruleDiff.added.length > 0 || ruleDiff.modified.length > 0 || ruleDiff.removed.length > 0) && (
						<div className="dsv-update__rule-changes" data-testid="dsv-changes-rule-diff" style={{ marginTop: 24 }}>
							<h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Conditional Rule Changes</h3>
							{ruleDiff.added.map((rule) => (
								<div key={rule.id} className="dsv-update__change-item" data-testid="dsv-changes-rule-added">
									<FieldStatusBadge status="New" />
									<span className="dsv-update__change-name">{summarizeRule(rule)}</span>
								</div>
							))}
							{ruleDiff.modified.map(({ rule }) => (
								<div key={rule.id} className="dsv-update__change-item" data-testid="dsv-changes-rule-modified">
									<FieldStatusBadge status="Modified" />
									<span className="dsv-update__change-name">{summarizeRule(rule)}</span>
								</div>
							))}
							{ruleDiff.removed.map((rule) => (
								<div key={rule.id} className="dsv-update__change-item" data-testid="dsv-changes-rule-removed">
									<FieldStatusBadge status="Deleted" />
									<span className="dsv-update__change-name">{summarizeRule(rule)}</span>
								</div>
							))}
						</div>
					)}
				</div>
			)}

			{selectedTab === 'impact' && (
				<div className="dsv-update__impact">
					<div className="dsv-update__impact-summary">
						<h3>Impact Analysis</h3>
						<p>
							This version change will affect{' '}
							<strong>{impactAnalysis?.subscribedOrganizations || 0}</strong> organisations
							currently using this data element.
						</p>
						{impactAnalysis && (
							<>
								<p>Risk Score: <strong>{impactAnalysis.riskScore}</strong></p>
								<p>Integrations Count: <strong>{impactAnalysis.integrationsCount}</strong></p>
								<p>Estimated Migration Time: <strong>{impactAnalysis.estimatedMigrationTime}</strong></p>
							</>
						)}
					</div>
				</div>
			)}

			{/* Field Modal */}
			<FieldModal
			visible={visibleFieldModal}
			hide={() => { hideFieldModal(); setEditingParentFieldId(null); }}
			field={editingField}
			onSave={handleSaveField}
			parentFieldId={editingParentFieldId}
			currentDepth={editingParentFieldId ? getFieldDepth(editingParentFieldId) + 1 : 0}
		/>

			{/* Confirm Version Modal */}
			<ConfirmVersionModal
				visible={visibleConfirmModal}
				hide={hideConfirmModal}
				versionInfo={isPureCreateMode
				? { elementName: newDataElementName, currentVersion: '—', newVersion: '1.0.0', changeType: 'INITIAL', changeDescription }
				: { elementName, currentVersion, newVersion, changeType, changeDescription }}
				schemaDiff={diffAgainstBase(localFields, baseSchemaForDiff)}
				payloadPreview={buildPayloadTemplate(localFields, baseSchemaForDiff)}
				schemaPreview={finalSchema}
				ruleDiff={ruleDiff}
				onConfirm={handleSubmitVersion}
			/>

			{/* Promote Modal */}
			<PromoteModal
				visible={visiblePromoteModal}
				hide={hidePromoteModal}
				version={selectedVersion}
				onConfirm={handleConfirmPromote}
			/>

			{/* Schema Validation Errors */}
			{schemaValidationErrors.length > 0 && (
				<div className="dsv-schema__validation-errors" data-testid="schema-validation-errors" style={{ margin: '12px 0', padding: 12, border: '1px solid #fca5a5', borderRadius: 6, backgroundColor: '#fef2f2' }}>
					<p style={{ fontWeight: 600, color: '#dc2626', marginBottom: 4 }}>Schema Validation Errors:</p>
					<ul style={{ margin: 0, paddingLeft: 20 }}>
						{schemaValidationErrors.map((err, i) => (
							<li key={i} style={{ color: '#dc2626', fontSize: 13 }}>{err}</li>
						))}
					</ul>
				</div>
			)}

			{/* Conflict Modal (409) */}
			<DSModal visible={visibleConflictModal} close={hideConflictModal} variant="small" dismissClickOutsideModal={false}>
				<DSCardConfigure style={{ minWidth: 400 }}>
					<DSCardConfigure.Header>
						<DSCardConfigure.Header.Title>Conflict Detected</DSCardConfigure.Header.Title>
						<DSCardConfigure.Header.Close>
							<button className="dsv-icon-btn" onClick={hideConflictModal} title="Close">&times;</button>
						</DSCardConfigure.Header.Close>
					</DSCardConfigure.Header>
					<DSCardConfigure.Body>
						<DSCardConfigure.Body.Col>
							<p data-testid="conflict-message">This draft has been modified by another user. Please reload to get the latest version.</p>
						</DSCardConfigure.Body.Col>
					</DSCardConfigure.Body>
					<DSCardConfigure.Footer>
						<DSCardConfigure.Footer.Left />
						<DSCardConfigure.Footer.Right>
							<button className="dsv-btn dsv-btn--ghost" onClick={hideConflictModal}>Cancel</button>
							<button className="dsv-btn dsv-btn--primary" style={{ marginLeft: rem(12) }} onClick={handleConflictReload} data-testid="conflict-reload-btn">
								Reload
							</button>
						</DSCardConfigure.Footer.Right>
					</DSCardConfigure.Footer>
				</DSCardConfigure>
			</DSModal>

			{/* Loading overlay */}
			{loading &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src="/img/loading.gif" alt="loading" />
						</div>
					</div>,
					document.body
				)}

			<Toast toastList={toastList} />
		</>
	);
};

export default DSVUpdateSchemaPage;
